import { useState } from 'react';
import { Check, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { COUNTRIES, type CountryCode } from '@/shared/countries';
import { FlagIcon } from '@/shared/countries/FlagIcon';
import { defaultLandingForProfile, useSession } from '@/shared/auth/SessionProvider';
import { cn } from '@/shared/lib/cn';

/**
 * Country switcher for the sidebar footer. Renders only for users whose
 * `paises_permitidos` contains more than one entry (cross-region admins).
 *
 * Two visual variants depending on whether the sidebar is collapsed:
 *   • collapsed → flag-only button (informative + clickable)
 *   • expanded  → small gear icon, sits inline next to the user's name.
 *     The active flag is shown next to the displayName separately, so the
 *     gear stays minimal — no redundant flag inside this trigger.
 *
 * Picking a different country opens a confirmation dialog: switching wipes
 * the React Query cache (so AR data can't leak into MX or vice versa), and
 * any in-progress work on the current page is lost. On confirm we redirect
 * to the user's default landing (Home for most roles, /administracion for
 * Nova, /mantenimiento for the mantenimiento role) — the same place
 * post-login would put them — so they restart from a known-clean state
 * under the new country.
 */
export function CountrySwitcher({ collapsed }: { collapsed?: boolean }) {
  const { code, config, available, canSwitch, setCountry } = useActiveCountry();
  const { profile } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<CountryCode | null>(null);

  if (!canSwitch) return null;

  const pendingConfig = pending ? COUNTRIES[pending] : null;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title={`Cambiar país (actual: ${config.name})`}
            aria-label={`Cambiar país (actual: ${config.name})`}
            className={cn(
              'inline-flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
              collapsed
                ? 'mx-auto h-10 w-10 border bg-card'
                : 'h-6 w-6',
            )}
          >
            {collapsed ? (
              <FlagIcon code={config.code} title={config.name} size={14} />
            ) : (
              <Globe className="h-3.5 w-3.5" aria-hidden />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="min-w-[180px] p-1">
          <ul className="flex flex-col">
            {available.map((c) => {
              const conf = COUNTRIES[c];
              const active = c === code;
              return (
                <li key={c}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      if (!active) setPending(c);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors',
                      active
                        ? 'bg-secondary text-foreground'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                    )}
                  >
                    <FlagIcon code={conf.code} title={conf.name} size={14} />
                    <span className="flex-1 font-medium">{conf.name}</span>
                    {active && <Check className="h-3.5 w-3.5" aria-hidden />}
                  </button>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>

      <Dialog open={pending !== null} onOpenChange={(next) => !next && setPending(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pendingConfig && (
                <FlagIcon code={pendingConfig.code} title={pendingConfig.name} size={16} />
              )}
              Cambiar país a {pendingConfig?.name ?? ''}
            </DialogTitle>
            <DialogDescription>
              Vas a cambiar el país activo. Cualquier cosa que tengas a medio
              cargar se va a descartar y se volverá a la pantalla de inicio
              bajo el país nuevo.
            </DialogDescription>
          </DialogHeader>
          <DialogBody />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (!pending) return;
                const next = pending;
                setPending(null);
                setCountry(next);
                navigate(defaultLandingForProfile(profile), { replace: true });
              }}
            >
              Cambiar país
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
